/**
 * STAGE 03 — DESKEW / perspective rectification (port of deskew.py).
 *
 * On the background-removed image (document on white), find the document outline
 * as the largest non-white blob. If it simplifies to a convex 4-gon that covers
 * >0.85 of the contour area → four_point_warp ("perspective"). Else fall back to
 * a min-area-rect rotation ("rotate"). Else "none".
 *
 * Skips when non-white coverage > 0.97 (no clean doc-vs-white boundary), or the
 * blob area < 0.10·frame. Thresholds match deskew.py exactly.
 */
import { ImagePixels } from '../../core/ports';
import { cloneImage, cropImage, toGray, createMask } from '../core/image';
import {
  arcLength,
  approxPolyDP,
  isConvex,
  polygonArea,
  minAreaRect,
} from '../core/geometry';
import { close } from '../core/morphology';
import { largestBlob } from '../core/contour';
import { fourPointWarp, rotateAffine } from '../core/warp';

export type DeskewMethod = 'perspective' | 'rotate' | 'none';

/** Port of _doc_contour: largest blob of (gray < 245), with non-white coverage. */
function docContour(img: ImagePixels) {
  const W = img.width, H = img.height;
  const gray = toGray(img);
  const mask = createMask(W, H);
  let count = 0;
  for (let i = 0; i < W * H; i++) {
    if (gray[i] < 245) {
      mask.data[i] = 255;
      count++;
    }
  }
  if (count < 200) return { blob: null, coverage: 1.0 };
  const coverage = count / (W * H);
  const closed = close(mask, 5);
  const blob = largestBlob(closed);
  return { blob, coverage };
}

export function deskew(img: ImagePixels): { image: ImagePixels; method: DeskewMethod } {
  const { blob, coverage } = docContour(img);
  if (!blob || coverage > 0.97) return { image: cloneImage(img), method: 'none' };
  const W = img.width, H = img.height;
  const area = blob.area;
  if (area < 0.1 * H * W) return { image: cloneImage(img), method: 'none' };

  // Try a convex 4-corner quad → perspective warp.
  const contour = blob.boundary;
  const peri = arcLength(contour, true);
  for (const eps of [0.02, 0.03, 0.04, 0.05, 0.06]) {
    const approx = approxPolyDP(contour, eps * peri);
    if (approx.length === 4 && isConvex(approx)) {
      if (polygonArea(approx) > 0.85 * area) {
        const warped = fourPointWarp(img, approx);
        if (warped && warped.width > 0 && warped.height > 0) {
          return { image: warped, method: 'perspective' };
        }
      }
      break; // first eps that yields a 4-gon decides (matches Python's break)
    }
  }

  // Fallback: straighten by rotation only.
  const rect = minAreaRect(contour);
  let angle = rect.angle;
  // Normalise to (-45, 45], matching the Python angle handling.
  while (angle <= -45) angle += 90;
  while (angle > 45) angle -= 90;
  if (Math.abs(angle) < 0.5) return { image: cloneImage(img), method: 'none' };
  const rot = rotateAffine(img, rect.cx, rect.cy, angle);

  // Crop to the non-white content after rotation.
  const g2 = toGray(rot);
  let x0 = W, y0 = H, x1 = -1, y1 = -1, cnt = 0;
  for (let i = 0; i < W * H; i++) {
    if (g2[i] < 245) {
      const x = i % W, y = (i / W) | 0;
      if (x < x0) x0 = x;
      if (x > x1) x1 = x;
      if (y < y0) y0 = y;
      if (y > y1) y1 = y;
      cnt++;
    }
  }
  if (cnt > 200 && x1 >= x0 && y1 >= y0) {
    return { image: cropImage(rot, x0, y0, x1, y1), method: 'rotate' };
  }
  return { image: rot, method: 'rotate' };
}
